import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { UserName, OfferedReportSummaryHistoryEntry } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../utils/numberUtils';

interface SumHistoryPopoverProps {
    history: OfferedReportSummaryHistoryEntry[];
    users: UserName[];
    offeredCluster: {
        worktypeName: string;
        channelName: string;
        taskName: string;
    };
}

function SumHistoryPopover({ history, users, offeredCluster }: SumHistoryPopoverProps) {
    const { t } = useTranslation();

    const getUserName = (userId: number): string => {
        const user = users.find((u) => u.id === userId);
        return user ? user.name : t('field.user');
    };

    const getUserNames = (userId: number | number[] | null): string => {
        if (userId === null || userId === undefined) {
            return t('common.unknown');
        }
        if (Array.isArray(userId)) {
            if (userId.length === 0) return t('common.unknown');
            const names = userId.map((id) => getUserName(id));
            return names.join(', ');
        }
        return getUserName(userId);
    };

    const getUserIds = (userId: number | number[] | null): string => {
        if (userId === null || userId === undefined) {
            return '';
        }
        if (Array.isArray(userId)) {
            if (userId.length === 0) return '';
            return `(ids: ${userId.join(', ')})`;
        }
        return `(id: ${userId})`;
    };

    const formatDate = (dateString: string): string => {
        return dayjs(dateString).format('DD.MM.YYYY');
    };

    const formatDelta = (entry: OfferedReportSummaryHistoryEntry): string => {
        // Both fields are SQL aggregates (COUNT / SUM), which pg returns as strings even though the
        // shared type says `number`. Coerce before comparing, or `=== 0` and `=== 1` never match:
        // a reverted change printed "2 changes: 0" instead of "no change", and a single change was
        // always pluralised. Formatting matters too — this is the only unseparated number left on
        // the Volume Overview screen.
        const delta = Number(entry.delta);
        const changeCount = Number(entry.changeCount);
        if (delta === 0) return t('common.noChange');
        const sign = delta > 0 ? '+' : '';
        const changeLabel = changeCount === 1 ? t('common.change') : t('common.changes');
        return `${changeCount} ${changeLabel}: ${sign}${formatNumber(delta)}`;
    };

    return (
        <Box sx={{ minWidth: 350, maxWidth: 450 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', paddingTop: '.5em' }}>
                {t('field.changes')}
            </Typography>

            {/* Show cluster info */}
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.secondary',
                    marginBottom: '1em',
                    paddingX: 1,
                }}
            >
                {offeredCluster.worktypeName} - {offeredCluster.channelName} - {offeredCluster.taskName}
            </Typography>

            {history && history.length > 0 ? (
                <Box sx={{ paddingX: 2 }}>
                    {history.map((entry, index) => {
                        const isFirst = index === 0;
                        const isLast = index === history.length - 1;
                        // First entry without isCurrent = initial values
                        const changeText = isFirst && !entry.isCurrent ? t('field.initialSum') : formatDelta(entry);

                        return (
                            <Box
                                key={index}
                                sx={{
                                    paddingY: 0.75,
                                    borderBottom: !isLast ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                                }}
                            >
                                {/* Line 1: Date - description + value */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <Typography variant="body2" component="span">
                                        <strong>{formatDate(entry.changeDate)}</strong>
                                        <span style={{ color: 'var(--grey)', marginLeft: '0.5em' }}>
                                            - {changeText}
                                        </span>
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                            fontWeight: entry.isCurrent ? 'bold' : 'normal',
                                            color: entry.isCurrent ? 'var(--primary)' : 'inherit',
                                            marginLeft: 2,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {entry.runningTotal}
                                    </Typography>
                                </Box>

                                {/* Line 2: User name(s) + [current] tag */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {getUserNames(entry.userId)}
                                        {getUserIds(entry.userId) && (
                                            <span style={{ marginLeft: '0.5em' }}>{getUserIds(entry.userId)}</span>
                                        )}
                                    </Typography>
                                    {entry.isCurrent && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'var(--primary)',
                                                fontWeight: 'bold',
                                                marginLeft: 2,
                                            }}
                                        >
                                            {t('common.current')}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <Typography variant="body2" sx={{ textAlign: 'center', padding: 1, color: 'text.secondary' }}>
                    {t('common.noChangesAvailable')}
                </Typography>
            )}
        </Box>
    );
}

export default SumHistoryPopover;
